// =============================================================================
// LP Express — Product Hero (PDP) template content model.
//
// A "Product Hero" landing page is a flagship direct-to-consumer physical-
// product sales page (ported from the template library's sales.product-hero):
// a two-column hero (product image gallery on the left, a buy box on the right
// with rating, benefit bullets, price, optional variant selector, add-to-cart
// CTA, reassurance line and trust chips) → a press/trust logo bar → a benefit
// grid → a "how it works" steps section → a reviews wall (REAL reviews) with a
// derived aggregate rating → a guarantee block → an FAQ accordion → a closing
// dark CTA band → legal disclaimer → a sticky bottom CTA bar (mobile).
//
// The whole page's COPY is generated in a single shot by a dedicated Sim
// Studio workflow (SIM_LP_PRODUCT_HERO_URL — a placeholder id until the
// workflow is built), then SPLIT by the server into per-section
// generatedContent keys (phBuyBox, phTrustBar, phBenefits, phHowItWorks,
// phReviews, phGuarantee, phFaq, phFinalCta, phDisclaimer, phStickyCta — see
// `splitLpProductHeroContent`). Those keys back the ordered, reorderable
// section model in shared/lp-express-sections.ts (LP_PRODUCT_HERO_SECTION_TYPES),
// so the page flows through the SAME sections pipeline as long-form-v2:
// getLpSections order, moveSection reorder, per-section hide/delete, and
// per-section copy regeneration (the single workflow re-runs and the server
// merges back ONLY the requested section — same model as the advertorial).
// Copy is edited in place through the SAME generatedContent path-based
// `setText` ops the other templates use — e.g.
// ["phBenefits","items",0,"title"].
//
// IMAGERY: the buy-box gallery LEADS with two AI lifestyle shots
// (LP_PRODUCT_HERO_IMAGE_SLOTS below → generatedContent.productHeroImages)
// generated through the workflow — the main image defaults to the first AI
// shot once its render lands — and the LAST carousel slot is always the
// brand's REAL product photo (swappable only via the media picker/upload,
// never AI-generated; it also serves as the main-image fallback while the AI
// shots are still pending). Reviews always come from the
// brand's REAL review data (via the review picker) — the workflow's copy never
// fabricates customer reviews. Prices, ratings, review counts, discounts and
// savings figures are likewise DERIVED from the real product/offer/sale/review
// data at render time — the workflow's copy never invents numbers, and discount
// framing only appears when a real active discount exists on the run.
//
// This module is pure + dependency-free so it runs identically on the client
// (renderer + editor) and the server (generation/normalization).
//
// ─── Sim workflow contract (SIM_LP_PRODUCT_HERO_URL) ─────────────────────────
//
// INPUT (top-level fields on the Start block — called with wrapInput=false,
// identical to the quiz/listicle/advertorial workflow envelopes):
//   {
//     "brandInfo":      string  // serialized brand doctrine/profile
//     "personaInfo":    string  // serialized persona persuasion profile
//     "productInfo":    string  // serialized product/offer attributes
//     "narrativeAngle": string  // serialized narrative angle
//     "offerInfo":      string  // serialized offer/pricing/sale context
//     "feedback":       string  // optional free-form regeneration steering
//     "currentCopy":    string  // optional JSON of the CURRENT productHero
//                               // blob (set on regeneration so the workflow
//                               // refines instead of starting from scratch)
//   }
//
// OUTPUT: a single JSON object (may be ```json-fenced under output.content —
// the extractor strips fences) matching LpProductHeroContent below. snake_case
// aliases are accepted for every camelCase key. Guidance for the workflow:
//   • Buy-box: a punchy product `headline`, a one-line `subhead`, 3–5 short
//     `heroBullets` (benefit-led), an add-to-cart `ctaText` and a reassurance
//     `ctaSub` line, plus 2–4 trust `chips` (shipping / returns / guarantee).
//   • `benefits.items`: 3–6 cards ({ title, body }) — the icon is chosen by the
//     renderer (lucide), so the workflow's `ic` field is ignored.
//   • `how.items`: 3–4 numbered steps ({ title, body }).
//   • `reviews.items`: 3 testimonial cards ({ name, role, text, rating }) in
//     persona voice — clearly editable placeholders that operators are expected
//     to replace with REAL reviews via the review picker when the brand has
//     review data.
//   • `faq.items`: 4–6 Q&A pairs.
//   • Do NOT put prices, ratings, review counts or dollar amounts in ANY copy
//     field — those are derived from real data at render time.
// =============================================================================

export interface LpProductHeroBenefit {
  ic: string; // raw icon hint from the workflow (ignored — renderer uses lucide)
  title: string;
  body: string;
}

export interface LpProductHeroStep {
  title: string;
  body: string;
}

// One review-wall card. Generated in persona voice by the Sim workflow and
// fully editable in the builder. When the brand has REAL review data, each card
// can be swapped for a real review via the standard review picker (the picked
// review is snapshotted on reviewOverrides and wins over the blob item) — real
// reviews are preferred for FTC compliance, but a brand with zero reviews still
// gets a complete, editable review block.
export interface LpProductHeroReview {
  name: string;
  role: string; // e.g. "Verified buyer" ("" = none)
  text: string;
  rating: number; // 1–5 stars
}

export interface LpProductHeroFaq {
  q: string;
  a: string;
}

export interface LpProductHeroProductDetailSpec {
  label: string;
  value: string;
}

/**
 * Optional Studio PDP detail copy. It remains nested under phBuyBox when the
 * canonical page is split so existing Product Hero section regeneration and
 * editing can carry it without introducing another LP section type.
 */
export interface LpProductHeroProductDetails {
  label: string;
  heading: string;
  description: string;
  specs: LpProductHeroProductDetailSpec[];
}

export interface LpProductHeroContent {
  eyebrow: string; // small kicker above the headline ("" = none)
  badge: string; // small badge, e.g. "Bestseller" ("" = none)
  headline: string; // product name / hero headline
  subhead: string; // short product sub-line ("" = none)
  heroBullets: string[]; // buy-box benefit bullets (up to 6)
  variantLabel: string; // variant selector label ("" = no selector)
  variantOptions: string[]; // variant options
  ctaText: string; // add-to-cart CTA label
  ctaSub: string; // reassurance line under the CTA ("" = none)
  chips: string[]; // trust chips row (shipping/returns)
  productDetails?: LpProductHeroProductDetails;
  trust: { label: string; items: string[] }; // press/trust logo bar
  benefits: { heading: string; dek: string; items: LpProductHeroBenefit[] };
  how: { heading: string; items: LpProductHeroStep[] };
  reviews: { heading: string; items: LpProductHeroReview[] };
  guarantee: {
    sealTop: string;
    sealBottom: string;
    heading: string;
    body: string;
  };
  faq: { heading: string; items: LpProductHeroFaq[] };
  final: { heading: string; dek: string; ctaText: string };
  disclaimer: string;
  stickyCtaText: string; // sticky bottom bar CTA label
}

// ── Normalization ────────────────────────────────────────────────────────────

function asObj(v: unknown): Record<string, unknown> {
  return v && typeof v === "object" && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : {};
}

function str(v: unknown, fallback = ""): string {
  return typeof v === "string" ? v : typeof v === "number" ? String(v) : fallback;
}

function strArray(v: unknown, cap = 20): string[] {
  return Array.isArray(v)
    ? v.filter((x) => typeof x === "string" && (x as string).trim()).slice(0, cap)
    : [];
}

// Accept either a plain string array or the template-library's wrapped
// { items: [...] } object (hero_bullets / chips arrive wrapped).
function itemsArray(v: unknown, cap: number): string[] {
  if (Array.isArray(v)) return strArray(v, cap);
  const o = asObj(v);
  return strArray(o.items, cap);
}

// Normalize a raw product-hero payload (e.g. from the Sim workflow, which may
// use snake_case) into the canonical LpProductHeroContent shape. Missing fields
// become empty strings/arrays so the renderer/editor never crash — the caller
// decides whether an empty result counts as a generation failure
// (isLpProductHeroContentUsable). IMPORTANT: any new editable field must be
// added BOTH here and to the interfaces above, or the renderer will drop it.
export function normalizeLpProductHeroContent(raw: unknown): LpProductHeroContent {
  const o = asObj(raw);
  const meta = asObj(o.meta);

  const varSrc = asObj(o.variants ?? o.variant);
  const trustSrc = asObj(o.trust);

  const beSrc = asObj(o.benefits);
  const benefits = {
    heading: str(beSrc.heading),
    dek: str(beSrc.dek),
    items: (Array.isArray(beSrc.items) ? beSrc.items : [])
      .map((b) => {
        const bo = asObj(b);
        return {
          ic: str(bo.ic ?? bo.icon),
          title: str(bo.title ?? bo.heading),
          body: str(bo.body ?? bo.text),
        };
      })
      .filter((b) => b.title || b.body)
      .slice(0, 6),
  };

  const howSrc = asObj(o.how);
  const how = {
    heading: str(howSrc.heading),
    items: (Array.isArray(howSrc.items) ? howSrc.items : [])
      .map((s) => {
        const so = asObj(s);
        return { title: str(so.title ?? so.heading), body: str(so.body ?? so.text) };
      })
      .filter((s) => s.title || s.body)
      .slice(0, 6),
  };

  const revSrc = asObj(o.reviews ?? o.reviews_block ?? o.reviewsBlock);
  const reviews = {
    heading: str(revSrc.heading),
    items: (Array.isArray(revSrc.items)
      ? revSrc.items
      : Array.isArray(revSrc.testimonials)
        ? revSrc.testimonials
        : Array.isArray(o.testimonials)
          ? o.testimonials
          : []
    )
      .map((t) => {
        const to = asObj(t);
        const ratingRaw = Number(to.rating ?? to.stars ?? 5);
        return {
          name: str(to.name ?? to.reviewer ?? to.author),
          role: str(to.role ?? to.title ?? to.meta),
          text: str(to.text ?? to.quote ?? to.review_text ?? to.reviewText),
          rating: Number.isFinite(ratingRaw)
            ? Math.max(1, Math.min(5, Math.round(ratingRaw)))
            : 5,
        };
      })
      .filter((t) => t.text)
      .slice(0, 3),
  };

  const guSrc = asObj(o.guarantee);
  const guarantee = {
    sealTop: str(guSrc.sealTop ?? guSrc.seal_top),
    sealBottom: str(guSrc.sealBottom ?? guSrc.seal_bottom),
    heading: str(guSrc.heading),
    body: str(guSrc.body ?? guSrc.text),
  };

  const faqSrc = asObj(o.faq);
  const faq = {
    heading: str(faqSrc.heading),
    items: (Array.isArray(faqSrc.items) ? faqSrc.items : [])
      .map((f) => {
        const fo = asObj(f);
        return {
          q: str(fo.q ?? fo.question),
          a: str(fo.a ?? fo.answer ?? fo.body),
        };
      })
      .filter((f) => f.q || f.a)
      .slice(0, 8),
  };

  const finalSrc = asObj(o.final ?? o.closing ?? o.final_cta ?? o.finalCta);
  const final = {
    heading: str(finalSrc.heading),
    dek: str(finalSrc.dek ?? finalSrc.subheadline),
    ctaText: str(finalSrc.ctaText ?? finalSrc.cta_text ?? finalSrc.text),
  };

  const stSrc = asObj(o.sticky ?? o.sticky_bar ?? o.stickyBar);
  const productDetailsSrc = asObj(
    o.productDetails ?? o.product_details,
  );
  const productDetails = {
    label: str(productDetailsSrc.label),
    heading: str(productDetailsSrc.heading),
    description: str(
      productDetailsSrc.description ?? productDetailsSrc.body,
    ),
    specs: (Array.isArray(productDetailsSrc.specs)
      ? productDetailsSrc.specs
      : []
    )
      .map((spec) => {
        const item = asObj(spec);
        return {
          label: str(item.label),
          value: str(item.value),
        };
      })
      // A partial spec is not safe customer-facing copy. Omit it instead of
      // guessing either half of the label/value pair.
      .filter((spec) => spec.label.trim() && spec.value.trim())
      .slice(0, 4),
  };
  const hasProductDetails = Boolean(
    productDetails.label ||
      productDetails.heading ||
      productDetails.description ||
      productDetails.specs.length,
  );

  return {
    eyebrow: str(o.eyebrow ?? meta.eyebrow),
    badge: str(o.badge ?? meta.badge),
    headline: str(o.headline ?? meta.headline),
    subhead: str(o.subhead ?? o.dek ?? o.subheadline ?? meta.subhead),
    heroBullets: itemsArray(o.heroBullets ?? o.hero_bullets ?? o.bullets, 6),
    variantLabel: str(o.variantLabel ?? o.variant_label ?? varSrc.label),
    variantOptions: strArray(o.variantOptions ?? o.variant_options ?? varSrc.options, 12),
    ctaText: str(o.ctaText ?? o.cta_text) || "Add to cart",
    ctaSub: str(o.ctaSub ?? o.cta_sub),
    chips: itemsArray(o.chips, 5),
    ...(hasProductDetails ? { productDetails } : {}),
    trust: { label: str(trustSrc.label), items: strArray(trustSrc.items, 8) },
    benefits,
    how,
    reviews,
    guarantee,
    faq,
    final,
    disclaimer: str(o.disclaimer),
    stickyCtaText:
      str(o.stickyCtaText ?? o.sticky_cta_text ?? str(stSrc.text)) ||
      str(o.ctaText ?? o.cta_text) ||
      "Add to cart",
  };
}

// ── Per-section split / assembly ─────────────────────────────────────────────
// The single-workflow output is split into one generatedContent key per
// section block (matching LP_PRODUCT_HERO_SECTION_TYPES in
// shared/lp-express-sections.ts) so the page participates in the ordered
// sections model: reorder, hide/delete, and per-section copy regeneration.

// Buy-box copy slice (gallery imagery + price/rating live OUTSIDE this copy —
// they are derived from real product/offer/review data at render time).
export interface LpProductHeroBuyBox {
  eyebrow: string;
  badge: string;
  headline: string;
  subhead: string;
  heroBullets: string[];
  variantLabel: string;
  variantOptions: string[];
  ctaText: string;
  ctaSub: string;
  chips: string[];
  productDetails?: LpProductHeroProductDetails;
}

// The per-section generatedContent keys, in canonical page order. Must stay in
// lockstep with LP_PRODUCT_HERO_SECTION_TYPES in shared/lp-express-sections.ts.
export const LP_PRODUCT_HERO_BLOCK_KEYS = [
  "phBuyBox",
  "phTrustBar",
  "phBenefits",
  "phHowItWorks",
  "phReviews",
  "phGuarantee",
  "phFaq",
  "phFinalCta",
  "phDisclaimer",
  "phStickyCta",
] as const;
export type LpProductHeroBlockKey = (typeof LP_PRODUCT_HERO_BLOCK_KEYS)[number];

// Split a normalized whole-page content object into its per-section
// generatedContent keys. Inverse of assembleLpProductHeroContent.
export function splitLpProductHeroContent(
  content: LpProductHeroContent,
): Record<LpProductHeroBlockKey, unknown> {
  const buyBox: LpProductHeroBuyBox = {
    eyebrow: content.eyebrow,
    badge: content.badge,
    headline: content.headline,
    subhead: content.subhead,
    heroBullets: content.heroBullets,
    variantLabel: content.variantLabel,
    variantOptions: content.variantOptions,
    ctaText: content.ctaText,
    ctaSub: content.ctaSub,
    chips: content.chips,
    ...(content.productDetails
      ? { productDetails: content.productDetails }
      : {}),
  };
  return {
    phBuyBox: buyBox,
    phTrustBar: content.trust,
    phBenefits: content.benefits,
    phHowItWorks: content.how,
    phReviews: content.reviews,
    phGuarantee: content.guarantee,
    phFaq: content.faq,
    phFinalCta: content.final,
    phDisclaimer: { text: content.disclaimer },
    phStickyCta: { ctaText: content.stickyCtaText },
  };
}

// Re-assemble the whole-page content shape from a generatedContent object that
// carries the per-section keys. Tolerant of missing/partial keys (returns
// normalized empties) — callers gate usability via
// isLpProductHeroContentUsable. Used by the renderer/editor to read one
// canonical shape, and by the server to build `currentCopy` for regeneration.
export function assembleLpProductHeroContent(gc: unknown): LpProductHeroContent {
  const o = asObj(gc);
  const buyBox = asObj(o.phBuyBox);
  const disclaimer = asObj(o.phDisclaimer);
  const sticky = asObj(o.phStickyCta);
  return normalizeLpProductHeroContent({
    ...buyBox,
    variants: {
      label: (buyBox as { variantLabel?: unknown }).variantLabel,
      options: (buyBox as { variantOptions?: unknown }).variantOptions,
    },
    trust: o.phTrustBar,
    benefits: o.phBenefits,
    how: o.phHowItWorks,
    reviews: o.phReviews,
    guarantee: o.phGuarantee,
    faq: o.phFaq,
    final: o.phFinalCta,
    disclaimer: (disclaimer as { text?: unknown }).text,
    stickyCtaText: (sticky as { ctaText?: unknown }).ctaText,
  });
}

// True when a generatedContent object carries the per-section product-hero
// keys with enough structure to render (see isLpProductHeroContentUsable).
export function hasUsableLpProductHeroSections(gc: unknown): boolean {
  const o = asObj(gc);
  if (!o.phBuyBox) return false;
  return isLpProductHeroContentUsable(assembleLpProductHeroContent(o));
}

// True when a normalized product-hero carries enough structure to render a real
// PDP (a headline plus at least three substantive supporting blocks across the
// buy-box bullets, benefit grid, how-it-works steps and FAQ). Used by the
// generator to reject an empty Sim response instead of persisting a blank page.
export function isLpProductHeroContentUsable(
  productHero: LpProductHeroContent | null | undefined,
): boolean {
  if (!productHero) return false;
  const hasHeadline = !!productHero.headline;
  const supporting =
    productHero.heroBullets.length +
    productHero.benefits.items.length +
    productHero.how.items.length +
    productHero.faq.items.length;
  return hasHeadline && supporting >= 3;
}

// ── AI lifestyle image slots ─────────────────────────────────────────────────
// AI-generated LIFESTYLE shots that LEAD the buy-box gallery carousel, stored
// under `generatedContent.productHeroImages` as
// [{ key, imageUrl, imageStatus, altText }] and generated per-slot through the
// standard LP Express single-slot image pipeline (same shape as
// listicleImages/advertorialImages, so retry + startup-resume ride the
// existing plumbing). The LAST carousel slot is always a real product photo —
// it doubles as the main-image fallback while these slots are still pending.
// Prompts come from the long-form hero-image Sim workflow (variantHint-driven,
// product reference photo as visual anchor).
export const LP_PRODUCT_HERO_IMAGE_SLOTS: { key: string; variantHint: string }[] = [
  {
    key: "lifestyle-1",
    variantHint:
      "lifestyle-in-use — a person using the product in a natural everyday setting; warm authentic light, editorial photography, product clearly recognizable, no text overlays",
  },
  {
    key: "lifestyle-2",
    variantHint:
      "lifestyle-context — the product in its real-world environment (where it lives or gets used), a different scene and angle than the in-use shot; warm natural light, no people required, no text overlays",
  },
];
